import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { MainLayout } from '../../Components/Layouts/MainLayout/MainLayout';
import { getInitials } from '../../Utils';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import {
  AttachmentsContainer,
  Container,
  NameContainer,
  StyledAvatar,
  SubTitle,
  TabsContainer,
  Title,
  TitleContainer,
} from './styles';
import Tab from '@mui/material/Tab';
import { CustomBadge } from '../../Components/CustomBadge/CustomBadge';
import { CandidateTable } from './Components/CandidateTable/CandidateTable';
import { JobStatusBox } from './Components/JobStatusBox/JobStatusBox';
import { Divider } from '@mui/material';
import { Dropzone } from '../../Components/Dropzone/Dropzone';
import { AttachmentsBox } from '../../Components/AttachmentsBox/AttachmentsBox';
import { JobSummary } from './Components/JobSummary/JobSummary';
import { JobTimeline } from './Components/JobTimeline/JobTimeline';
import { JobMetrics } from './Components/JobMetrics/JobMetrics';
import { useJob } from '../../Hooks/useJob';
import { useCallback, useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { createJobNote, getJobFiles, getJobNotes, uploadJobFile } from '../../Services/Jobs';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { NotesBox } from '../../Components/NotesBox/NotesBox';
import { JobStatusDialog } from '../../Components/Dialogs/JobStatusDialog/JobStatusDialog';
import { COUNT_ROWS_PER_PAGE, JobStatus, StatusType } from '../../Types/constants';
import { getCandidateJobs } from '../../Services/CandidateJobs';
import { useDebounce } from '../../Hooks/useDebounce';
import { useFilterCandidateJobSubStatus } from '../../Hooks/useFilterCandidateJobSubStatus';
import { JobStatusSelect } from '../../Components/StatusSelect/JobStatusSelect';
import { JobTeam } from './Components/JobTeam/JobTeam';
import { Loader } from '../../Components/Loader/Loader';
import { useAuthStore } from '../../Stores/useAuthStore';

export const JobDetail = () => {
  const { jobId: id } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { job, selectedTab, handleChange, updateJob } = useJob();
  const { user } = useAuthStore();
  const [files, setFiles] = useState<File[]>([]);
  const [showJobStatusDialog, setShowJobStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState<StatusType>('');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [candidateJobStatus, setCandidateJobStatus] = useState<string>('all');
  const [candidateJobSubStatus, setCandidateJobSubStatus] = useState<string>('all');
  const [clearSearch, setClearSearch] = useState(false);
  const [loadingFiles, setIsLoadingFiles] = useState(false);
  const [candidateJobList, setCandidateJobList] = useState<any[]>([]);

  const { candidateJobSubStatusOptions } = useFilterCandidateJobSubStatus(
    candidateJobStatus,
    setCandidateJobSubStatus,
  );

  const errorMessage = t('toast.errors.something_went_wrong');

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const { data: jobFiles, isLoading: isLoadingAttachments } = useQuery({
    queryKey: ['getJobFiles'],
    queryFn: () =>
      getJobFiles(id!, {
        page: 1,
        count: 100,
      }),
    enabled: !!id,
  });

  const fetchCandidateJobs = useCallback(
    async ({ pageParam = 1 }) => {
      const params: any = {
        page: pageParam,
        count: COUNT_ROWS_PER_PAGE,
        jobId: id,
        jobReference: job?.jobReference || '',
      };

      // Solo agregar parámetros si tienen valores válidos
      if (debouncedSearchTerm) {
        params.candidateName = debouncedSearchTerm;
      }
      if (candidateJobStatus && candidateJobStatus !== 'all') {
        params.status = candidateJobStatus;
      }
      if (candidateJobSubStatus && candidateJobSubStatus !== 'all') {
        params.subStatus = candidateJobSubStatus;
      }

      return await getCandidateJobs(params);
    },
    [id, job?.jobReference, debouncedSearchTerm, candidateJobStatus, candidateJobSubStatus],
  );

  const {
    data: candidateJobsData,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      'getCandidateJobs',
      id,
      debouncedSearchTerm,
      candidateJobStatus,
      candidateJobSubStatus,
    ],
    queryFn: fetchCandidateJobs,
    getNextPageParam: (lastPage) => {
      const nextPage = (lastPage.page || 0) + 1;
      return nextPage <= (lastPage.lastPage || 0) ? nextPage : undefined;
    },
    initialPageParam: 1,
    enabled: !!id && !!job,
  });

  const { mutate: uploadJobFileMutation } = useMutation({
    mutationFn: (data: FormData) => uploadJobFile(id, data),
    onError: (_error: any) => {
      toast.error(t('toast.errors.something_went_wrong'));
      setIsLoadingFiles(false);
    },
    onSuccess: (_data: any) => {
      toast.success(t('toast.success.upload'));
      queryClient.invalidateQueries({ queryKey: ['getJobFiles'] });
      setFiles([]);
      setIsLoadingFiles(false);
    },
  });

  const handleFileUpload = () => {
    setIsLoadingFiles(true);
    const formData = new FormData();
    formData.append('file', files[0]);
    uploadJobFileMutation(formData);
  };

  const { data: jobNotes, isLoading: isNotesLoading } = useQuery({
    queryKey: ['getJobNotes'],
    queryFn: () =>
      getJobNotes(id!, {
        page: 1,
        count: 100,
      }),
    enabled: !!id,
  });

  const { mutate: createJobNotaMutation } = useMutation({
    mutationFn: (description: string) => createJobNote(id!, { description, userId: user?.id! }),
    onError: (_error: any) => {
      toast.error(errorMessage);
    },
    onSuccess: (_data: any) => {
      queryClient.invalidateQueries({ queryKey: ['getJobNotes'] });
    },
  });

  const handleSuccessEditNote = () => {
    queryClient.invalidateQueries({ queryKey: ['getJobNotes'] });
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCandidateJobStatus('all');
    setCandidateJobSubStatus('all');
    setClearSearch(true);
  };

  const canEditJob = () => {
    return job.status !== JobStatus.Closed && job.status !== JobStatus.Canceled;
  };

  // Limpiar la lista y refetch cuando cambian los filtros
  useEffect(() => {
    setCandidateJobList([]);
    refetch();
  }, [debouncedSearchTerm, candidateJobStatus, candidateJobSubStatus, refetch]);

  // Manejar la lista de candidate jobs con scroll infinito
  useEffect(() => {
    const allCandidateJobs = candidateJobsData?.pages.flatMap((page) => page.items) || [];

    if (allCandidateJobs.length === 0) {
      setCandidateJobList([]);
      return;
    }

    setCandidateJobList(allCandidateJobs);
  }, [candidateJobsData]);

  return (
    <MainLayout noPadding>
      {job && (
        <>
          <Container>
            <NameContainer>
              <StyledAvatar>{getInitials(job.position)}</StyledAvatar>
              <TitleContainer>
                <Title>{job.jobReference} - {job.position}</Title>
                <SubTitle>
                  <AccountBalanceIcon style={{ marginRight: '8px' }} />
                  {job.department}
                  <Divider
                    orientation="vertical"
                    flexItem
                    component="span"
                    sx={{ marginLeft: '8px', marginRight: '8px', height: '20px' }}
                  />
                  {(job.currency || '') +
                    ' ' +
                    (job.minimumSalary ? job.minimumSalary + ' - ' : '') +
                    (job.currency || '') +
                    ' ' +
                    (job.maximumSalary || '')}
                  <Divider
                    orientation="vertical"
                    flexItem
                    component="span"
                    sx={{ marginLeft: '8px', marginRight: '8px', height: '20px' }}
                  />
                  <GroupAddIcon style={{ marginRight: '8px' }} />
                  {job.headcount}
                </SubTitle>
              </TitleContainer>
              <JobStatusSelect
                hideAll
                status={job.status}
                disabled={!canEditJob()}
                handleChange={(newValue: StatusType) => {
                  setNewStatus(newValue);
                  setShowJobStatusDialog(true);
                }}
              />
            </NameContainer>
            <JobStatusBox
              talentAcquisitionScreening={job.summaryMetrics?.talentAcquisitionScreeningCount}
              inProgressTalentAcquisition={job.summaryMetrics?.inProgressTalentAcquisitionCount}
              inProgressClient={job.summaryMetrics?.inProgressClientCount}
            />
          </Container>
          <TabsContainer
            value={selectedTab}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            <Tab
              {...a11yProps(0)}
              label={
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <span>Candidates</span>
                  {candidateJobsData && candidateJobsData.pages.length > 0 && (candidateJobsData.pages[candidateJobsData.pages.length - 1]?.totalItems || 0) > 0 && (
                    <CustomBadge label={candidateJobsData.pages[candidateJobsData.pages.length - 1]?.totalItems || 0} />
                  )}
                </div>
              }
            />
            <Tab label="Summary" {...a11yProps(1)} />
            <Tab label="Notes" {...a11yProps(2)} />
            <Tab label="Attachments" {...a11yProps(3)} />
            <Tab label="Timeline" {...a11yProps(4)} />
            <Tab label="Metrics" {...a11yProps(5)} />
            <Tab label="Team" {...a11yProps(6)} />
          </TabsContainer>
          <div style={{ padding: 20 }}>
            {selectedTab === 0 && candidateJobsData && (
              <CandidateTable
                onInvalidateQuery={() => {
                  queryClient.invalidateQueries({
                    queryKey: [
                      'getCandidateJobs',
                      id,
                      debouncedSearchTerm,
                      candidateJobStatus,
                      candidateJobSubStatus,
                    ],
                  });
                  refetch();
                }}
                jobStatus={job.status}
                subStatusOptions={candidateJobSubStatusOptions}
                status={candidateJobStatus}
                subStatus={candidateJobSubStatus}
                handleSearch={handleSearch}
                onStatusChange={(newStatus: string) => {
                  setCandidateJobStatus(newStatus);
                }}
                onSubStatusChange={(newStatus: string) => {
                  setCandidateJobSubStatus(newStatus);
                }}
                handleClearFilters={handleClearFilters}
                clearSearch={clearSearch}
                rows={candidateJobList}
                totalItems={candidateJobsData?.pages[candidateJobsData.pages.length - 1]?.totalItems || 0}
                loadMore={() => {
                  if (hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                  }
                }}
                loading={isFetchingNextPage}
                hasMore={!!hasNextPage}
              />
            )}
            {selectedTab === 1 && <JobSummary job={job} updateJob={updateJob} />}
            {selectedTab === 2 && (
              <NotesBox
                createNote={createJobNotaMutation}
                containerWidth="50%"
                notes={jobNotes}
                isLoading={isNotesLoading}
                preventEdit={!canEditJob()}
                onSuccessEdit={handleSuccessEditNote}
              />
            )}
            {selectedTab === 3 && (
              <AttachmentsContainer>
                <AttachmentsBox files={jobFiles ?? []} preventEdit={!canEditJob()} isLoading={isLoadingAttachments} />
                {canEditJob() && (
                  <Dropzone onFileUpload={handleFileUpload} files={files} setFiles={setFiles} loading={loadingFiles} />
                )}
              </AttachmentsContainer>
            )}
            {selectedTab === 4 && (
              <JobTimeline status={job.status} creationDate={job.creationDateTime} />
            )}
            {selectedTab === 5 && <JobMetrics jobId={job.id} />}
            {selectedTab === 6 && <JobTeam jobId={job.id} canEdit={canEditJob()} />}
          </div>
        </>
      )}
      <JobStatusDialog
        open={showJobStatusDialog}
        handleClose={() => setShowJobStatusDialog(false)}
        newStatus={newStatus}
        lastStatus={job?.lastStatus}
      />
      <Loader isLoading={isLoading} />
    </MainLayout>
  );
};